import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import { applyPermissionBasedFiltering } from '../../../../components/ColumnManagement';
import { COMPANY_VIEW_TYPE } from '../../../../data-models/view-config.data-model';
import { ColumnMeta } from '../../../../types';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { selectedColumnsGridDisplayFieldsOrgState } from '../../../AdminPanel/DataDisplay/DataDisplayState';
import { CompanyRT } from '../../components/OverviewTable/columns/PredefinedColumns/CompanyNameAndLogo/CompanyRT';
import { PredefinedColumns } from '../../components/OverviewTable/columns/PredefinedColumns/PredefinedColumns';
import {
  displayedFormulaFieldIdsLoadable,
  formulaFieldsListLoadable,
} from '../../../../services/state/AdminPanel/FormulaFieldsStateJ';
import {
  displayedFormulaFieldsForEntity,
  formulaFieldEntityForView,
  formulaFieldToColumnMeta,
} from '../../../../util/formula-fields';
import { columnNameToDisplayNameState, columnsMetaState, selectedViewPFJ } from '../../state/ViewStateJ';

export function filterOutUnwantedColumns(columnsMeta: ColumnMeta[], columnsToHide?: string[]) {
  const unwantedColumnNamesMap: Record<string, string> = {
    CompanyName: 'CompanyName',
    LogoUrl: 'LogoUrl',
    [CompanyRT.NAME]: CompanyRT.NAME,
  };

  if (!columnsMeta.length) {
    return [];
  }

  let filteredColumnMeta = columnsMeta.filter(
    (column) => column.visible && !unwantedColumnNamesMap[column.name]
  );

  if (columnsToHide && columnsToHide.length > 0) {
    filteredColumnMeta = applyPermissionBasedFiltering(filteredColumnMeta, columnsToHide);
  }

  return filteredColumnMeta.map((column) => ({
    ...column,
    description: column?.description || column.displayName || '',
  }));
}

export function useColumns(): ColumnMeta[] {
  const selectedView = useAtomValue(selectedViewPFJ);
  const companyViewType = selectedView?.companyViewType;
  const resolvedCompanyViewType = companyViewType ?? COMPANY_VIEW_TYPE.PORTFOLIO_OVERVIEW;
  const columnsMeta = useAtomValue(columnsMetaState(resolvedCompanyViewType));
  const columnsToHide = useAtomValue(selectedColumnsGridDisplayFieldsOrgState);
  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  const displayedLoad = useAtomValue(displayedFormulaFieldIdsLoadable);
  const { showAdminManageAccessColumnGridDisplay } = useFlags<MaggieFeatureFlags>();

  // FD-2976: depend on the primitive `companyViewType` instead of the whole
  // `selectedView`. The body only reads `companyViewType`, so depending on
  // the full object emits a fresh array on every `viewsPFJ` write and
  // cascades into `ColumnsDefinitionProvider`.
  return useMemo(() => {
    if (!companyViewType) {
      return [];
    }
    const approvedColumns = filterOutUnwantedColumns(
      columnsMeta,
      showAdminManageAccessColumnGridDisplay ? columnsToHide : undefined
    );
    const predefinedColumns = PredefinedColumns.getAllMeta(companyViewType);
    // Formula fields are FE-injected: their values are materialized onto the
    // view's rows (row.formulaData.<key>), so scope each view to the entity
    // whose rows its evaluator runs on.
    const formulaEntity = formulaFieldEntityForView(companyViewType);
    const formulaColumns =
      formulaEntity != null && formulaLoad.state === 'hasData' && displayedLoad.state === 'hasData'
        ? displayedFormulaFieldsForEntity(
            formulaLoad.data ?? [],
            formulaEntity,
            displayedLoad.data ?? new Set<number>()
          ).map(formulaFieldToColumnMeta)
        : [];
    return [...predefinedColumns, ...approvedColumns, ...formulaColumns];
  }, [
    columnsMeta,
    companyViewType,
    columnsToHide,
    showAdminManageAccessColumnGridDisplay,
    formulaLoad,
    displayedLoad,
  ]);
}

export function useColumnMetaMap() {
  const selectedView = useAtomValue(selectedViewPFJ);
  const companyViewType = selectedView?.companyViewType ?? COMPANY_VIEW_TYPE.PORTFOLIO_OVERVIEW;
  return useAtomValue(columnNameToDisplayNameState(companyViewType));
}
